import { DeviationQuestionUserResponseDto } from 'src/generated/services/TFinancialApi';
import { QuestionFieldConfig } from './questionMapper';

/**
 * Processes user answers and converts them to form default values
 */
export const processAnswersToDefaultValues = (answers: DeviationQuestionUserResponseDto[], fieldConfigs: QuestionFieldConfig[]): Record<string, any> => {
  if (!answers || answers.length === 0) {
    return {};
  }

  const defaultValues: Record<string, any> = {};

  answers.forEach((answer) => {
    if (!answer.questionId) {
      return;
    }

    // Find the corresponding field config
    const fieldConfig = fieldConfigs.find((config) => config.id === answer.questionId || config.id === answer.answerOptionId);

    if (!fieldConfig) {
      return;
    }

    switch (answer.questionType) {
      case 'TEXT':
        // For text fields, use the field config ID (which is now the answer option ID) as key
        if (answer.questionId && fieldConfig) {
          defaultValues[fieldConfig.id] = answer.answerText || '';
        }
        break;

      case 'DATE_RANGE':
        // For date range, parse the JSON string and convert to Date objects
        if (answer.answerOptionId && answer.answerText) {
          try {
            const parsedDateRange = JSON.parse(answer.answerText);
            // Convert string dates to Date objects for the date picker
            defaultValues[answer.answerOptionId] = {
              start: new Date(parsedDateRange.start),
              end: new Date(parsedDateRange.end),
            };
          } catch (error) {
            console.warn('Failed to parse date range:', answer.answerText, error);
            defaultValues[answer.answerOptionId] = null;
          }
        }
        break;

      case 'DATE':
      case 'DATE_TIME':
        // For date fields, parse the date string
        if (answer.answerText) {
          defaultValues[answer.questionId] = new Date(answer.answerText);
        }
        break;

      case 'RADIO':
      case 'SELECT':
        // For radio/select, use answerOptionId as the selected value
        if (answer.answerOptionId) {
          // Special handling for SELECT_A_CHARGE_NAME: store as JSON string with answerText
          if (fieldConfig?.domainType === 'SELECT_A_CHARGE_NAME' && answer.answerText) {
            defaultValues[answer.questionId] = JSON.stringify({
              answerOptionId: answer.answerOptionId,
              answerText: answer.answerText,
            });
          } else {
            defaultValues[answer.questionId] = answer.answerOptionId;
          }
        }
        break;

      case 'MULTIPLE_SELECT':
        // For multi-select, build an array of selected values
        if (answer.answerOptionId) {
          if (!defaultValues[answer.questionId]) {
            defaultValues[answer.questionId] = [];
          }
          defaultValues[answer.questionId].push(answer.answerOptionId);
        }
        break;

      default:
        // Fallback: use questionId as key and answerText or answerOptionId as value
        defaultValues[answer.questionId] = answer.answerText || answer.answerOptionId;
    }
  });

  return defaultValues;
};

/**
 * Converts form values back to DeviationQuestionUserResponseDto format
 */
export const processFormValuesToAnswers = (formValues: Record<string, any>, fieldConfigs: QuestionFieldConfig[]): DeviationQuestionUserResponseDto[] => {
  const answers: DeviationQuestionUserResponseDto[] = [];

  Object.entries(formValues).forEach(([fieldId, value]) => {
    if (!value || value === '') {
      return;
    }

    const fieldConfig = fieldConfigs.find((config) => config.id === fieldId);
    if (!fieldConfig) {
      return;
    }

    switch (fieldConfig.type) {
      case 'RADIO':
      case 'SELECT': {
        let answerOptionId = String(value);
        let answerText: string | undefined;

        // Handle special case where SELECT value might be a JSON string (e.g., from RhfFieldSelectSubChargeDomain)
        if (typeof value === 'string' && value.startsWith('{')) {
          try {
            const parsed = JSON.parse(value);
            if (parsed.answerOptionId) {
              answerOptionId = parsed.answerOptionId;
            }
            if (parsed.answerText) {
              answerText = parsed.answerText;
            }
          } catch (e) {
            // If parsing fails, use the original value
            console.warn('Failed to parse SELECT value as JSON:', value, e);
          }
        }

        const radioSelectAnswer: any = {
          answerOptionId,
        };
        // Include answerText if it was extracted from JSON
        if (answerText) {
          radioSelectAnswer.answerText = answerText;
        }
        answers.push(radioSelectAnswer);
        break;
      }

      case 'TEXT': {
        const textAnswer = {
          answerText: String(value),
          answerOptionId: fieldId,
        };
        answers.push(textAnswer);
        break;
      }

      case 'DATE_RANGE':
        answers.push({
          questionId: fieldConfig.id,
          answerOptionId: fieldId,
          answerText: JSON.stringify(value),
          questionType: fieldConfig.type,
        });
        break;

      case 'DATE':
      case 'DATE_TIME':
        answers.push({
          questionId: fieldId,
          answerText: value instanceof Date ? value.toISOString() : String(value),
          questionType: fieldConfig.type,
        });
        break;

      case 'MULTIPLE_SELECT': {
        // Handle array of selected values
        if (Array.isArray(value)) {
          value.forEach((selectedValue) => {
            const selectedOption = fieldConfig.options?.find((opt) => opt.id === selectedValue);
            if (selectedOption) {
              answers.push({
                questionId: fieldId,
                answerOptionId: selectedValue,
                answerText: selectedOption.label,
                questionType: fieldConfig.type,
              });
            }
          });
        }
        break;
      }

      default:
        // Fallback for unknown types
        console.warn(`Unknown field type: ${fieldConfig.type}`);
        break;
    }
  });

  return answers;
};
